import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import SEO from "@/components/SEO";
import Breadcrumbs, { buildBreadcrumbSchema } from "@/components/Breadcrumbs";
import { Newspaper, Calendar as CalIcon, Tag } from "lucide-react";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { settings } = useSettings();
  const siteName = settings.site_name || "HyderabadTourism.in";
  const siteUrl = settings.site_url || (typeof window !== "undefined" ? window.location.origin : "");

  useEffect(() => {
    api.get("/blog").then((r) => setPosts(r.data)).finally(() => setLoading(false));
  }, []);

  const breadcrumbItems = [
    { label: "Home", to: "/" },
    { label: "Blog" },
  ];

  return (
    <div data-testid="blog-page">
      <SEO
        title="Travel Blog — Hyderabad Tourism Insider Guides"
        description="Insider guides, itineraries and travel tips for tours from Hyderabad. Written by our travel team."
        schemas={[
          buildBreadcrumbSchema(breadcrumbItems),
          {
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": `${siteName} Travel Blog`,
            "description": "Insider guides, itineraries and travel tips for tours from Hyderabad.",
            "url": typeof window !== "undefined" ? window.location.href : `${siteUrl}/blog`,
            "publisher": { "@type": "Organization", "name": siteName },
          },
        ]}
      />
      <section className="bg-gradient-to-b from-secondary/40 to-background py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 rounded-xl bg-teal text-white flex items-center justify-center">
              <Newspaper className="w-5 h-5" />
            </div>
            <div className="text-xs uppercase tracking-[0.2em] text-teal font-semibold">Travel Blog</div>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mb-3">Insider guides & itineraries</h1>
          <p className="text-muted-foreground max-w-2xl">Deep-dive articles from our travel team. Save money, plan smart, travel better.</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Breadcrumbs items={breadcrumbItems} />
        {loading ? (
          <div className="text-center text-muted-foreground py-16">Loading...</div>
        ) : posts.length === 0 ? (
          <div className="text-center text-muted-foreground py-16" data-testid="blog-empty">No posts yet — check back soon.</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((p) => (
              <Link
                key={p.id}
                to={`/blog/${p.slug}`}
                className="group bg-white border border-border rounded-2xl overflow-hidden card-hover"
                data-testid={`blog-card-${p.slug}`}
              >
                {p.cover_image && (
                  <div className="aspect-[16/9] overflow-hidden">
                    <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </div>
                )}
                <div className="p-5">
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
                    <span className="flex items-center gap-1"><CalIcon className="w-3 h-3" />{formatDate(p.published_at)}</span>
                    {p.tags?.[0] && <span className="flex items-center gap-1"><Tag className="w-3 h-3" />{p.tags[0]}</span>}
                  </div>
                  <h3 className="font-display text-lg font-semibold mb-2 line-clamp-2 group-hover:text-teal transition-colors">{p.title}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-3">{p.excerpt}</p>
                  <div className="mt-4 text-sm text-teal font-medium">Read article →</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
